import {
  Permissions,
  type PermissionId,
} from "@/modules/roles/domain/contracts/permissions";
import {
  NOTIFICATION_ITEMS_PER_SECTION,
  NOTIFICATION_SIDEBAR_BADGE_ROUTES,
} from "../config";
import {
  countUnreadBySectionType,
  getNotificationDropdownRows,
  getUnreadNotificationCount,
  sectionKeyForType,
} from "./list-notifications";
import { syncQueueNotifications } from "./sync-queue-notifications";
import type { DbTransaction } from "@/shared/types";

export type NotificationSeverity = "normal" | "warning" | "critical";

export type NotificationSummaryItem = {
  id: string;
  type: string;
  title: string;
  body: string;
  href: string;
  createdAt: string;
  severity: NotificationSeverity;
  isUnread?: boolean;
};

export type NotificationSummarySection = {
  key: string;
  label: string;
  count: number;
  listHref: string;
  items: NotificationSummaryItem[];
};

export type NotificationSummary = {
  totalActionable: number;
  sections: NotificationSummarySection[];
  /** AN3 — unread count keyed by sidebar url */
  sidebarBadges: Record<string, number>;
};

const SECTION_META: Record<
  string,
  { label: string; listHref: string; group?: "wallet" }
> = {
  topup: { label: "ເຕີມເງິນ", listHref: "/app/wallet/topup", group: "wallet" },
  kyc: { label: "KYC", listHref: "/app/wallet/kyc-requests", group: "wallet" },
  suspicious: {
    label: "ຄວາມສົງໄສ",
    listHref: "/app/wallet/compliance",
  },
};

function trimSectionItems(
  sections: NotificationSummarySection[],
  maxItems: number,
): NotificationSummarySection[] {
  let remaining = maxItems;
  return sections.map((section) => {
    if (remaining <= 0) {
      return { ...section, items: [] };
    }
    const items = section.items.slice(0, remaining);
    remaining -= items.length;
    return { ...section, items };
  });
}

export async function getNotificationSummary(
  userId: string,
  permissions: PermissionId[],
  db: DbTransaction,
): Promise<NotificationSummary> {
  await syncQueueNotifications(db);

  const [dropdownRows, unreadCounts, totalActionable] = await Promise.all([
    getNotificationDropdownRows(userId, permissions, db),
    countUnreadBySectionType(userId, permissions, db),
    getUnreadNotificationCount(userId, permissions, db),
  ]);

  const itemsBySection = new Map<string, NotificationSummaryItem[]>();

  for (const row of dropdownRows) {
    const key = sectionKeyForType(row.type);
    const items = itemsBySection.get(key) ?? [];
    items.push({
      id: row.id,
      type: row.type,
      title: row.title,
      body: row.body ?? "",
      href: row.href,
      createdAt: row.createdAt,
      severity: row.severity,
      isUnread: row.isUnread,
    });
    itemsBySection.set(key, items);
  }

  const rawSections: NotificationSummarySection[] = [];

  for (const [key, meta] of Object.entries(SECTION_META)) {
    const count = unreadCounts.get(key) ?? 0;
    const items = itemsBySection.get(key) ?? [];
    if (count === 0 && items.length === 0) continue;

    rawSections.push({
      key,
      label: meta.label,
      count,
      listHref: meta.listHref,
      items: items.slice(0, NOTIFICATION_ITEMS_PER_SECTION),
    });
  }

  const walletSections = rawSections.filter(
    (s) => SECTION_META[s.key]?.group === "wallet",
  );
  const suspiciousSection = rawSections.find((s) => s.key === "suspicious");

  const grouped: NotificationSummarySection[] = [];

  if (walletSections.length > 0) {
    grouped.push({
      key: "wallet",
      label: "ກະເປົາເງິນ",
      count: walletSections.reduce((sum, s) => sum + s.count, 0),
      listHref: "/app/wallet/topup",
      items: walletSections.flatMap((s) => s.items),
    });
  }

  if (suspiciousSection && (suspiciousSection.count > 0 || suspiciousSection.items.length > 0)) {
    grouped.push(suspiciousSection);
  }

  const sections = trimSectionItems(grouped, 10);

  const sidebarBadges: Record<string, number> = {};
  for (const [sectionKey, count] of unreadCounts) {
    if (count <= 0) continue;
    const route = NOTIFICATION_SIDEBAR_BADGE_ROUTES[sectionKey];
    if (route) sidebarBadges[route] = (sidebarBadges[route] ?? 0) + count;
  }

  return {
    totalActionable,
    sections,
    sidebarBadges,
  };
}

/** Permissions ที่เปิดใช้ bell */
export const NOTIFICATION_BELL_PERMISSIONS: PermissionId[] = [
  Permissions.top_up.list,
  Permissions.compliance.read,
  Permissions.suspicious.list,
];
